import { ChatContext } from "context/ChatContext";
import { IUnreadChat } from "hooks/useUserChatWebSocket";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UserChatService from "services/chatService/UserChatService";
import ChatHeaderOpenChat from "./ChatHeaderOpenChat";
import UserChatBody from "./UserChatBody";
import UserChatInput from "./UserChatInput";
import { ChatStatusButton } from "./ChatStatusButton";
import { initilaizeChats, updateChatStatus } from "redux/UserChatRedux";

function OpenChat({
  sidePanel = false,
  setChatIsOpen,
  selectedChat,
  setSelectedChat,
}: any) {
  const dispatch = useDispatch<any>();
  const {
    messages,
    createNewChat,
    sendMessage,
    notifyTyping,
    showChatProgress,
    latestChat,
    changeChatStatus,
    changeMessageStatus,
    updatedMessageStatus,
    setUpdatedMessageStatus,
    setUnreadChats,
    chatUpdateBlockStatus,
  } = useContext(ChatContext);
  const { allChats } = useSelector((state: any) => state?.userChat);
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const [chatMessages, setChatMessages] = useState<any>();
  const [inputValue, setInputValue] = useState("");
  const [firstMessage, setFirstMessage] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showTyping, setShowTyping] = useState(false);
  const [pagination, setPagination] = useState(1);

  // NEW: Track scroll state
  const isAutoScrolling = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);

  // NEW: Detect manual user scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Skip if we're auto-scrolling
      if (isAutoScrolling.current) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Show button if user scrolled up more than 100px from bottom
      if (distanceFromBottom > 100) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
        setNewMessageCount(0); // Reset count when user scrolls to bottom
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (updatedMessageStatus.length > 0) {
      if (updatedMessageStatus[0] === selectedChat.id) {
        const updatedChat = {
          ...chatMessages,
          messages: [
            ...(chatMessages?.messages || []).map((chat: any) => {
              if (!chat.is_read) {
                return { ...chat, is_read: true };
              }
              return chat;
            }),
          ],
        };
        setChatMessages(updatedChat);
        setUpdatedMessageStatus([]);
      }
    }
  }, [updatedMessageStatus]);

  // MODIFIED: Track new messages when user has scrolled up
  useEffect(() => {
    if (messages?.chat_id == selectedChat?.id) {
      const updatedChat = {
        ...chatMessages,
        messages: [...(chatMessages?.messages || []), messages],
      };
      setChatMessages(updatedChat);

      // Check if user has scrolled up
      const container = containerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        if (distanceFromBottom > 100) {
          // User has scrolled up, don't auto-scroll but increment counter
          setNewMessageCount((prev) => prev + 1);
        } else {
          // User is at bottom, auto-scroll
          if (chatMessages != null) {
            setTimeout(() => {
              scrollToBottom();
            }, 100);
          }
        }
      }
    }

    setUnreadChats((prevChats: IUnreadChat[]) => {
      return prevChats.filter((chat: IUnreadChat) => {
        return chat.chatId != selectedChat.id;
      });
    });
  }, [messages]);

  useEffect(() => {
    if (
      chatMessages &&
      chatMessages.messages?.length > 0 &&
      chatMessages.messages[chatMessages.messages?.length - 1]?.user_id !=
        loggedInUser?.id &&
      !chatMessages.messages[chatMessages.messages?.length - 1]?.is_read &&
      selectedChat?.status !== "rejected" &&
      selectedChat?.block_status !== "blocked"
    ) {
      changeMessageStatus(selectedChat?.id, selectedChat?.receiver?.id);
    }
  }, [chatMessages]);

  useEffect(() => {
    const chatId = selectedChat?.id;
    if (
      showChatProgress?.chatId == chatId &&
      showChatProgress?.onChange &&
      showChatProgress?.userId != loggedInUser?.id
    ) {
      setShowTyping(true);

      // Only scroll if user hasn't scrolled up
      const container = containerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        if (distanceFromBottom < 100) {
          nearScrollToBottom(200);
        }
      }
    } else {
      setShowTyping(false);
    }
  }, [showChatProgress]);

  const getChatMessages = async (limit: number, page: number) => {
    if (selectedChat?.id != 0) {
      setChatMessages(null);
      UserChatService.getMessages(selectedChat?.id, limit, page).then(
        (res: any) => {
          let updated_messages = {
            ...res,
            messages: res?.messages?.reverse(),
          };
          setChatMessages(updated_messages);

          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
      );
    } else {
      setChatMessages([]);
    }
  };

  // fetch messages
  useEffect(() => {
    scrollToTop();
    getChatMessages(50, pagination);
    // Reset states when changing chats
    setShowScrollButton(false);
    setNewMessageCount(0);
  }, [selectedChat]);

  const sendMessageData = (message: string) => {
    if (selectedChat && selectedChat?.id !== 0) {
      const chatId = selectedChat?.id;
      const receiverId = selectedChat?.receiver?.id;

      sendMessage(chatId, receiverId, message);
      setFirstMessage(message);
    } else {
      setFirstMessage(message);
      createNewChat(selectedChat?.receiver?.id, selectedChat?.chat_name);
    }
    setInputValue("");

    // Always scroll to bottom when user sends a message
    setShowScrollButton(false);
    setNewMessageCount(0);
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  useEffect(() => {
    if (latestChat && selectedChat?.id === 0) {
      const a = selectedChat;

      const chatIndex = allChats?.findIndex((chat: any) => {
        return chat.id == latestChat?.id;
      });

      if (chatIndex !== -1) {
        let updatedChats = [...allChats];
        const updatedChat = {
          ...updatedChats[chatIndex],
          latest_message: { ...latestChat, content: firstMessage },
          id: latestChat.id,
          pivot: {
            user_id: loggedInUser?.id,
            chat_id: latestChat.id,
          },
        };

        updatedChats[chatIndex] = updatedChat;

        setSelectedChat(updatedChat);
        dispatch(initilaizeChats(updatedChats));
      }

      sendMessage(latestChat?.id, selectedChat?.receiver?.id, firstMessage);
    }
  }, [latestChat]);

  const loadMoreMessages = () => {
    UserChatService.getMessages(selectedChat?.id, 50, pagination + 1).then(
      (res: any) => {
        let updated_messages = {
          ...res,
          messages: [
            ...res?.messages?.reverse(),
            ...(chatMessages?.messages || []),
          ],
        };
        setChatMessages(updated_messages);
        setPagination(pagination + 1);
      }
    );
  };

  const scrollToBottom = () => {
    const container = containerRef.current;
    if (container) {
      isAutoScrolling.current = true;

      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });

      // Reset flag after animation completes
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 500);
    }
  };

  const scrollToTop = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const nearScrollToBottom = (threshold: number) => {
    const container = containerRef.current;
    if (container) {
      const currentScrollPosition =
        container.scrollTop + container.clientHeight;
      const maxScrollPosition = container.scrollHeight;

      if (maxScrollPosition - currentScrollPosition <= threshold) {
        isAutoScrolling.current = true;

        container.scrollTo({
          top: maxScrollPosition,
          behavior: "smooth",
        });

        setTimeout(() => {
          isAutoScrolling.current = false;
        }, 500);
      }
    }
  };

  // NEW: Handle scroll to bottom button click
  const handleScrollToLatest = () => {
    setShowScrollButton(false);
    setNewMessageCount(0);
    scrollToBottom();
  };

  const handleApproveChat = (status: string) => {
    changeChatStatus(selectedChat.id, selectedChat.receiver.id, status);
    setSelectedChat({
      ...selectedChat,
      status: status,
    });
  };

  const updateBlockChatStatusFromOpenChat = (status: "active" | "blocked") => {
    const res = chatUpdateBlockStatus(selectedChat.id, status);
    const { block_status, block_by, updated_at, ...otherData } = selectedChat;
    setSelectedChat({
      ...otherData,
      block_status: status,
      block_by:
        status === "blocked"
          ? [...block_by, loggedInUser?.id]
          : block_by.filter((id: number) => id !== loggedInUser?.id),
      updated_at: updated_at,
    });
  };

  return (
    <div
      className={`flex w-full flex-col text-white ${
        sidePanel ? `h-[95vh]` : `h-[85vh] sm:rounded-md`
      } scrollbar-hide overflow-y-scroll`}
    >
      <ChatHeaderOpenChat
        sidePanel={sidePanel}
        receiver={selectedChat?.receiver}
        setSelectedChat={setSelectedChat}
        setChatIsOpen={setChatIsOpen}
        handleApproveChat={handleApproveChat}
        chatStatus={selectedChat?.status}
        chatData={selectedChat}
        updateBlockChatStatusFromOpenChat={updateBlockChatStatusFromOpenChat}
      />

      {/* display messages */}
      <div
        ref={containerRef}
        className="scrollbar-hide relative flex-1 overflow-y-auto bg-[var(--Color1E1E1E)]"
        style={{ maxHeight: "calc(100% - 40px)" }}
      >
        <UserChatBody
          sidePanel={sidePanel}
          chatMessages={chatMessages}
          showChatProgress={showTyping}
          loadMoreMessages={loadMoreMessages}
        />
      </div>
      {/* NEW: Scroll to latest messages button */}
      {showScrollButton && (
        <button
          onClick={handleScrollToLatest}
          className="absolute bottom-36 right-8 z-10 flex items-center gap-2 rounded-full bg-[var(--red,#F50537)] px-5 py-2.5 text-sm font-medium text-white shadow-[0_8px_24px_rgba(0,0,0,0.4)] transition-colors hover:opacity-90"
          aria-label="Scroll to latest messages"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
          <span className="font-medium">
            {newMessageCount > 0
              ? `${newMessageCount} new message${
                  newMessageCount > 1 ? "s" : ""
                }`
              : "Scroll to latest"}
          </span>
        </button>
      )}
      {/* user input */}
      {(selectedChat?.status === "approved" &&
        selectedChat.block_status === "active") ||
      (["rejected", "pending"].includes(selectedChat?.status) &&
        selectedChat?.chat_name.split("-")[0] == loggedInUser?.id &&
        selectedChat.block_status !== "blocked") ||
      (selectedChat.block_status === "blocked" &&
        !selectedChat.block_by.includes(loggedInUser?.id)) ? (
        <div
          className={`bottom-1 w-full ${
            !sidePanel && `mt-3`
          } bg-[var(--Color1E1E1E)]`}
        >
          <UserChatInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            sendMessage={sendMessageData}
            userIndividualChat={true}
            notifyTyping={notifyTyping}
            selectedChat={selectedChat}
            sidePanel={sidePanel}
          />
        </div>
      ) : (
        <>
          <div className="mt-2 flex w-full items-center justify-between gap-3 border-t border-white/10 bg-white/[0.04] px-5 py-3 text-sm backdrop-blur-sm max-sm:flex-col max-sm:items-stretch max-sm:gap-2">
            <p className="text-white/70">
              To proceed, please choose to either accept or reject the chat.
            </p>
            {selectedChat?.status === "pending" &&
              selectedChat?.block_status !== "blocked" && (
                <div className="flex items-center justify-end gap-3 max-sm:gap-1">
                  <ChatStatusButton
                    handleApproveChat={handleApproveChat}
                    className="border-green-500/60 text-green-400 hover:bg-green-500/10"
                    text="Approve"
                    status="approved"
                  />
                  <ChatStatusButton
                    handleApproveChat={handleApproveChat}
                    className="border-red-500/60 text-red-400 hover:bg-red-500/10"
                    text="Reject"
                    status="rejected"
                  />
                </div>
              )}
            {selectedChat.status === "rejected" && (
              <ChatStatusButton
                handleApproveChat={handleApproveChat}
                className="border-green-500/60 text-green-400 hover:bg-green-500/10"
                text="Approve"
                status="approved"
              />
            )}

            {selectedChat.block_status === "blocked" &&
              selectedChat.block_by.includes(loggedInUser?.id) && (
                <ChatStatusButton
                  handleApproveChat={updateBlockChatStatusFromOpenChat}
                  className="border-green-500/60 text-green-400 hover:bg-green-500/10"
                  text="Unblock"
                  status="active"
                />
              )}
          </div>
        </>
      )}
    </div>
  );
}

export default OpenChat;